import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getAuthToken } from '../../services/api';
import './UserDashboard.css';

const API_BASE_URL = getApiBaseUrl();

const UserSectionPathBadges = ({
  summaryData = null,
  summaryLoading = false,
  summaryError = null,
  onRetrySummary,
  exploreCourses = [],
  onBackToDashboard,
  onViewSubscription
}) => {
  const { t } = useTranslation('user');
  const [view, setView] = useState('list');
  const [selectedPath, setSelectedPath] = useState(null);
  const [collection, setCollection] = useState(null);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectionError, setCollectionError] = useState(null);
  const [collectionRetryTick, setCollectionRetryTick] = useState(0);
  const [detailBadge, setDetailBadge] = useState(null);

  const paths = Array.isArray(summaryData?.paths) ? summaryData.paths : [];

  const pathTitle = (path) => {
    const explore = exploreCourses.find((c) => c.id === path.pathId || c.id === path.id);
    return explore?.title || path.titleKey || path.pathId;
  };

  const pathIcon = (path) => {
    const explore = exploreCourses.find((c) => c.id === path.pathId || c.id === path.id);
    return explore?.icon || '🏅';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatBadgeProgress = (badge) => {
    const progress = Number(badge?.progress) || 0;
    const target = Number(badge?.target) || 0;
    if (badge?.criteriaType === 'percent') {
      return t('user.pathBadges.percentProgress', { progress, target });
    }
    return t('user.pathBadges.countProgress', { progress, target });
  };

  const openPath = (path) => {
    setSelectedPath(path);
    setCollection(null);
    setCollectionError(null);
    setCollectionLoading(true);
    setDetailBadge(null);
    setView('collection');
  };

  useEffect(() => {
    if (view !== 'collection' || !selectedPath?.pathId) return;
    const token = getAuthToken();
    if (!token) {
      setCollection(null);
      setCollectionError('failed');
      setCollectionLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCollectionLoading(true);
    setCollectionError(null);
    fetch(`${API_BASE_URL}/api/me/path-badges/${encodeURIComponent(selectedPath.pathId)}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
    })
      .then(async (res) => {
        let data = null;
        try {
          data = await res.json();
        } catch {
          data = null;
        }
        if (cancelled) return;
        if (res.status === 403) {
          setCollection(null);
          setCollectionError('forbidden');
          return;
        }
        if (!res.ok || !data || data.success !== true) {
          setCollection(null);
          setCollectionError('failed');
          return;
        }
        setCollection(data);
        setCollectionError(null);
      })
      .catch(() => {
        if (!cancelled) {
          setCollection(null);
          setCollectionError('failed');
        }
      })
      .finally(() => {
        if (!cancelled) setCollectionLoading(false);
      });
    return () => { cancelled = true; };
  }, [view, selectedPath?.pathId, collectionRetryTick]);

  const renderList = () => (
    <div className="user-main-content">
      <div className="user-content-header">
        <div className="user-content-header-top">
          <button type="button" className="back-btn" onClick={onBackToDashboard}>
            <i className="material-icons">arrow_back</i>
            <span>{t('user.pathBadges.backToDashboard')}</span>
          </button>
          <h1 className="user-content-title">{t('user.pathBadges.myTitle')}</h1>
        </div>
        <p className="user-content-subtitle">{t('user.pathBadges.mySubtitle')}</p>
      </div>

      <div className="user-section">
        {summaryLoading ? (
          <div className="user-courses-loading">
            <p>{t('user.pathBadges.loading')}</p>
          </div>
        ) : summaryError ? (
          <div className="user-courses-empty path-badge-load-error">
            <i className="material-icons">error_outline</i>
            <h3>{t('user.pathBadges.loadError')}</h3>
            <p>{t('user.pathBadges.loadErrorHint')}</p>
            <button type="button" className="user-course-btn primary" onClick={onRetrySummary}>
              {t('user.pathBadges.retry')}
            </button>
          </div>
        ) : paths.length === 0 ? (
          <div className="user-courses-empty">
            <i className="material-icons">emoji_events</i>
            <h3>{t('user.pathBadges.emptyTitle')}</h3>
            <p>{t('user.pathBadges.emptyNote')}</p>
            <button type="button" className="user-course-btn primary" onClick={onViewSubscription || onBackToDashboard}>
              {t('user.myLearningPaths.viewSubscription')}
            </button>
          </div>
        ) : (
          <div className="user-courses-grid">
            {paths.map((path) => {
              const earned = Number(path.badgesEarned) || 0;
              const total = Number(path.badgesTotal) || 0;
              const remaining = Number(path.remaining) || Math.max(0, total - earned);
              const percent = Number(path.percent) || 0;
              return (
                <div key={path.pathId} className="user-course-card learning-path-card">
                  <div className="user-course-image">
                    <div className="placeholder">
                      <span className="explore-tab-icon">{pathIcon(path)}</span>
                    </div>
                  </div>
                  <div className="user-course-content">
                    <h3 className="user-course-title">{pathTitle(path)}</h3>
                    <div className="learning-path-stats">
                      <p className="learning-path-stat">
                        {t('user.pathBadges.earnedOfTotal', { earned, total })}
                      </p>
                      <p className="learning-path-stat">
                        {t('user.pathBadges.percentComplete', { percent })}
                      </p>
                      <p className="learning-path-stat">
                        {t('user.pathBadges.earnedRemaining', { earned, remaining })}
                      </p>
                      <div className="user-course-progress">
                        <div className="user-course-progress-bar">
                          <div className="user-course-progress-fill" style={{ width: `${Math.min(100, percent)}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="user-course-actions">
                      <button type="button" className="user-course-btn primary" onClick={() => openPath(path)}>
                        <i className="material-icons">emoji_events</i>
                        {t('user.pathBadges.viewBadges')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const badges = Array.isArray(collection?.badges) ? collection.badges : [];
  const collectionTitle = selectedPath ? pathTitle(selectedPath) : t('user.pathBadges.myTitle');
  const noneEarned = badges.length > 0 && badges.every((b) => !b.earned);

  const renderCollectionError = () => (
    <div className="user-courses-empty path-badge-load-error">
      <i className="material-icons">{collectionError === 'forbidden' ? 'lock' : 'error_outline'}</i>
      <h3>
        {collectionError === 'forbidden'
          ? t('user.pathBadges.accessDenied')
          : t('user.pathBadges.loadError')}
      </h3>
      <p>
        {collectionError === 'forbidden'
          ? t('user.pathBadges.accessDeniedHint')
          : t('user.pathBadges.loadErrorHint')}
      </p>
      {collectionError !== 'forbidden' && (
        <button
          type="button"
          className="user-course-btn primary"
          onClick={() => setCollectionRetryTick((n) => n + 1)}
        >
          {t('user.pathBadges.retry')}
        </button>
      )}
    </div>
  );

  const renderCollection = () => (
    <div className="user-main-content">
      <div className="user-content-header">
        <div className="user-content-header-top">
          <button
            type="button"
            className="back-btn"
            onClick={() => {
              setView('list');
              setSelectedPath(null);
              setDetailBadge(null);
              setCollectionError(null);
            }}
          >
            <i className="material-icons">arrow_back</i>
            <span>{t('user.pathBadges.backToPathBadges')}</span>
          </button>
          <h1 className="user-content-title">{t('user.pathBadges.collectionTitle', { path: collectionTitle })}</h1>
        </div>
        <p className="user-content-subtitle">{t('user.pathBadges.collectionSubtitle')}</p>
      </div>

      <div className="user-section">
        {collectionLoading ? (
          <div className="user-courses-loading">
            <p>{t('user.pathBadges.loading')}</p>
          </div>
        ) : collectionError ? (
          renderCollectionError()
        ) : (
          <>
            {noneEarned && (
              <div className="path-badge-empty-banner">
                <strong>{t('user.pathBadges.noneEarnedYet')}</strong>
                <p>{t('user.pathBadges.noneEarnedHint')}</p>
              </div>
            )}
            <div className="path-badge-collection">
              {badges.map((badge) => {
                const earned = badge.earned === true;
                return (
                  <button
                    key={badge.code}
                    type="button"
                    className={`path-badge-item ${earned ? 'earned' : 'locked'}`}
                    onClick={() => setDetailBadge(badge)}
                  >
                    <span className="path-badge-item-icon" aria-hidden="true">{earned ? badge.icon : '🔒'}</span>
                    <span className="path-badge-item-body">
                      <span className="path-badge-item-name">{badge.name}</span>
                      <span className="path-badge-item-desc">{badge.description}</span>
                      {earned ? (
                        <span className="path-badge-item-meta earned-label">
                          {t('user.pathBadges.earned')}
                          {badge.earnedAt ? ` · ${t('user.pathBadges.earnedOn', { date: formatDate(badge.earnedAt) })}` : ''}
                        </span>
                      ) : (
                        <span className="path-badge-item-meta">
                          {t('user.pathBadges.progress')}: {formatBadgeProgress(badge)}
                        </span>
                      )}
                      {!earned && (
                        <span className="path-badge-mini-bar">
                          <span style={{ width: `${Math.min(100, Number(badge.percentage) || 0)}%` }} />
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {detailBadge && (
        <div className="path-badge-detail-overlay" onClick={() => setDetailBadge(null)} role="presentation">
          <div
            className={`path-badge-detail-card ${detailBadge.earned ? 'earned' : 'locked'}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="path-badge-detail-title"
          >
            <button type="button" className="path-badge-detail-close" onClick={() => setDetailBadge(null)} aria-label={t('user.pathBadges.close')}>
              <i className="material-icons">close</i>
            </button>
            <div className="path-badge-detail-icon">{detailBadge.earned ? detailBadge.icon : '🔒'}</div>
            <h2 id="path-badge-detail-title">{detailBadge.name}</h2>
            <p className="path-badge-detail-path">{collectionTitle}</p>
            <p>{detailBadge.description}</p>
            <div className="path-badge-detail-progress">
              <span>{t('user.pathBadges.progress')}</span>
              <strong>{formatBadgeProgress(detailBadge)}</strong>
              {detailBadge.criteriaType === 'percent' ? (
                <span>{t('user.pathBadges.percentProgressHint', {
                  progress: Number(detailBadge.progress) || 0,
                  target: Number(detailBadge.target) || 0
                })}</span>
              ) : (
                <span>{t('user.pathBadges.percentComplete', { percent: detailBadge.percentage || 0 })}</span>
              )}
              <div className="user-course-progress">
                <div className="user-course-progress-bar">
                  <div className="user-course-progress-fill" style={{ width: `${Math.min(100, Number(detailBadge.percentage) || 0)}%` }} />
                </div>
              </div>
            </div>
            {detailBadge.earned ? (
              <p className="path-badge-earned-note">
                {t('user.pathBadges.earned')}
                {detailBadge.earnedAt ? ` · ${t('user.pathBadges.earnedOn', { date: formatDate(detailBadge.earnedAt) })}` : ''}
              </p>
            ) : (
              <p className="path-badge-locked-note">{t('user.pathBadges.keepLearning')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return view === 'collection' ? renderCollection() : renderList();
};

export default UserSectionPathBadges;
